import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { get, patch, post, del } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Avatar } from "@/components/common/Avatar";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Skeleton, EmptyState } from "@/components/common/EmptyState";
import { ConfirmDelete } from "@/components/common/ConfirmDelete";
import { SearchInput } from "@/components/common/SearchInput";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { MODULES } from "@/lib/modules";
import { cn } from "@/lib/utils";
import { Shield, ChevronRight, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ACTIONS = ["view", "create", "edit", "delete", "approve", "override"];
const MODKEYS = [...MODULES.map((m) => m.key), "settings"];

export default function UsersRoles() {
  const [tab, setTab] = useState("users");
  const [selectedRole, setSelectedRole] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userStatusFilter, setUserStatusFilter] = useState("all");
  const [roleSearch, setRoleSearch] = useState("");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { can } = useApp();

  const { data: users, isLoading: lu } = useQuery({ queryKey: ["users"], queryFn: () => get("/users") });
  const { data: roles, isLoading: lr } = useQuery({ queryKey: ["roles"], queryFn: () => get("/roles") });

  const filteredUsers = useMemo(() => {
    return (users || []).filter((u) => {
      if (userSearch && !`${u.name} ${u.email}`.toLowerCase().includes(userSearch.toLowerCase())) return false;
      if (userRoleFilter !== "all" && u.role_id !== userRoleFilter) return false;
      if (userStatusFilter !== "all" && u.status !== userStatusFilter) return false;
      return true;
    });
  }, [users, userSearch, userRoleFilter, userStatusFilter]);
  const filteredRoles = useMemo(() => {
    return (roles || []).filter((r) => !roleSearch || r.name?.toLowerCase().includes(roleSearch.toLowerCase()));
  }, [roles, roleSearch]);

  const updateUser = useMutation({
    mutationFn: ({ id, body }) => patch(`/users/${id}`, body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("User updated"); },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not update user"),
  });

  const removeUser = useMutation({
    mutationFn: (id) => del(`/users/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["users"] }); toast.success("User removed"); },
    onError: (err) => toast.error(err?.response?.data?.detail || "This user is protected and cannot be removed"),
  });

  const saveRole = useMutation({
    mutationFn: (r) => patch(`/roles/${r.id}`, { permissions: r.permissions, project_scope: r.project_scope }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["roles"] }); toast.success("Role permissions saved"); },
  });

  const togglePerm = (mod, action) => {
    setSelectedRole((r) => {
      const perms = { ...(r.permissions || {}) };
      const list = new Set(perms[mod] || []);
      list.has(action) ? list.delete(action) : list.add(action);
      perms[mod] = [...list];
      return { ...r, permissions: perms };
    });
  };

  return (
    <div>
      <PageHeader title="Users & Roles" subtitle="Manage team members and configure module-wise & project-wise access.">
        <div className="flex rounded-lg border border-border bg-card p-0.5">
          {["users", "roles"].map((t) => (
            <button key={t} data-testid={`ur-tab-${t}`} onClick={() => { setTab(t); setSelectedRole(null); }}
              className={cn("rounded-md px-4 py-1.5 text-sm font-medium capitalize transition-colors",
                tab === t ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")}>
              {t}
            </button>
          ))}
        </div>
        {tab === "users" && can("settings", "create") && (
          <Button data-testid="add-user-btn" onClick={() => navigate("/settings/users/new")}>
            <Plus className="mr-1.5 h-4 w-4" />Add User
          </Button>
        )}
      </PageHeader>

      {tab === "users" && (
        <>
          {users?.length > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <SearchInput testid="users-search" placeholder="Search name or email" value={userSearch} onChange={setUserSearch} />
              <Select value={userRoleFilter} onValueChange={setUserRoleFilter}>
                <SelectTrigger className="w-40" data-testid="users-role-filter"><SelectValue placeholder="All roles" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {roles?.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={userStatusFilter} onValueChange={setUserStatusFilter}>
                <SelectTrigger className="w-36" data-testid="users-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {!lu && users?.length > 0 && !filteredUsers.length ? (
            <EmptyState icon={Shield} title="No matching users" description="Adjust your search or filters." />
          ) : (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {lu ? <Skeleton className="m-4 h-64" /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Name</th>
                  <th className="px-4 py-3 font-semibold">Email</th>
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Department</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  {can("settings", "delete") && <th className="px-4 py-3"></th>}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Avatar user={u} size="sm" />
                        <span className="font-medium text-foreground">{u.name}</span>
                        {u.protected && <StatusBadge family="slate" label="Protected" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-3">
                      {can("settings", "edit") && !u.protected ? (
                        <Select value={u.role_id} onValueChange={(v) => updateUser.mutate({ id: u.id, body: { role_id: v } })}>
                          <SelectTrigger data-testid={`user-role-select-${u.id}`} className="h-8 w-40"><SelectValue /></SelectTrigger>
                          <SelectContent>{roles?.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : u.role?.name}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{u.department?.name}</td>
                    <td className="px-4 py-3">
                      {can("settings", "edit") && !u.protected ? (
                        <div className="flex items-center gap-2">
                          <Switch data-testid={`user-status-toggle-${u.id}`} checked={u.status === "active"}
                            onCheckedChange={(checked) => updateUser.mutate({ id: u.id, body: { status: checked ? "active" : "inactive" } })} />
                          <StatusBadge status={u.status} />
                        </div>
                      ) : <StatusBadge status={u.status} />}
                    </td>
                    {can("settings", "delete") && (
                      <td className="px-4 py-3 text-right">
                        {!u.protected && (
                          <ConfirmDelete testid={`del-user-${u.id}`} title={`Remove ${u.name}?`}
                            trigger={<button className="text-muted-foreground hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                            onConfirm={() => removeUser.mutate(u.id)} />
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
          )}
        </>
      )}

      {tab === "roles" && !selectedRole && (
        <>
          {roles?.length > 0 && (
            <div className="mb-4"><SearchInput testid="roles-search" placeholder="Search role name" value={roleSearch} onChange={setRoleSearch} /></div>
          )}
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          {lr ? <Skeleton className="m-4 h-64" /> : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Role</th>
                  <th className="px-4 py-3 font-semibold">Users</th>
                  <th className="px-4 py-3 font-semibold">Project Scope</th>
                  <th className="px-4 py-3 font-semibold">Type</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRoles.map((r) => (
                  <tr key={r.id} data-testid={`role-row-${r.id}`} onClick={() => setSelectedRole(r)}
                    className="cursor-pointer border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 font-medium text-foreground">
                        <Shield className="h-4 w-4 text-primary" />{r.name}
                      </div>
                      <div className="ml-6 text-xs text-muted-foreground">{r.description}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{r.user_count}</td>
                    <td className="px-4 py-3"><StatusBadge family={r.project_scope === "all" ? "violet" : "slate"} label={r.project_scope === "all" ? "All projects" : "Member only"} /></td>
                    <td className="px-4 py-3"><StatusBadge family={r.type === "system" ? "blue" : "slate"} label={r.type} /></td>
                    <td className="px-4 py-3 text-right"><ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        </>
      )}

      {tab === "roles" && selectedRole && (
        <div>
          <div className="mb-4 flex items-center justify-between">
            <button data-testid="back-to-roles" onClick={() => setSelectedRole(null)} className="text-sm text-primary hover:underline">← Back to roles</button>
            <Button data-testid="save-role" disabled={selectedRole.protected} onClick={() => saveRole.mutate(selectedRole)}>
              {selectedRole.protected ? "Protected role" : "Save permissions"}
            </Button>
          </div>
          <div className="mb-4 rounded-xl border border-border bg-card p-4">
            <div className="flex items-center gap-2 font-display text-lg font-semibold"><Shield className="h-5 w-5 text-primary" />{selectedRole.name}</div>
            <p className="mt-1 text-sm text-muted-foreground">{selectedRole.description}</p>
            <div className="mt-3 flex gap-2">
              {["all", "member"].map((s) => (
                <button key={s} data-testid={`scope-${s}`} disabled={selectedRole.protected}
                  onClick={() => setSelectedRole((r) => ({ ...r, project_scope: s }))}
                  className={cn("rounded-lg border px-3 py-1.5 text-xs font-medium capitalize",
                    selectedRole.project_scope === s ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground")}>
                  {s === "all" ? "All projects" : "Member projects only"}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3 font-semibold">Module</th>
                  {ACTIONS.map((a) => <th key={a} className="px-2 py-3 text-center font-semibold capitalize">{a}</th>)}
                </tr>
              </thead>
              <tbody>
                {MODKEYS.map((mod) => (
                  <tr key={mod} className="border-b border-border last:border-0">
                    <td className="px-4 py-2.5 font-medium capitalize text-foreground">{mod}</td>
                    {ACTIONS.map((a) => (
                      <td key={a} className="px-2 py-2.5 text-center">
                        <Checkbox
                          data-testid={`perm-${mod}-${a}`}
                          disabled={selectedRole.protected}
                          checked={(selectedRole.permissions?.[mod] || []).includes(a)}
                          onCheckedChange={() => togglePerm(mod, a)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
